import { createStore, combineReducers, compose, applyMiddleware } from 'redux';
import { reducer as formReducer } from 'redux-form';
import thunk from 'redux-thunk';
import authMiddleware from '@alphapoint/apex-web/lib/redux/middlewares/authMiddleware';
import placeOrderMiddleware from '@alphapoint/apex-web/lib/redux/middlewares/placeOrderMiddleware';
import startupMiddleware from '@alphapoint/apex-web/lib/redux/middlewares/startupMiddleware';
import wsConnectionMiddleware from '@alphapoint/apex-web/lib/redux/middlewares/wsConnectionMiddleware';
import orderFeeMiddleware from '@alphapoint/apex-web/lib/redux/middlewares/orderFeeMiddleware';
import priceQuantityIncrementMiddleware from '@alphapoint/apex-web/lib/redux/middlewares/priceQuantityIncrementMiddleware';
import level2monitorMiddleware from '@alphapoint/apex-web/lib/redux/middlewares/level2monitorMiddleware';
import snackOverrideMiddleware from '@alphapoint/apex-web/lib/redux/middlewares/snackOverrideMiddleware';
import languageMiddleware from './languageMiddleware';

// reducers (sort by name)
import api from '@alphapoint/apex-web/lib/redux/reducers/apiReducer';
import apexCore from '@alphapoint/apex-web/lib/redux/apexCore';
import affiliate from '@alphapoint/apex-web/lib/redux/reducers/affiliateReducer';
import apiKey from '@alphapoint/apex-web/lib/redux/reducers/apiKeyReducer';
import apiProcessing from '@alphapoint/apex-web/lib/redux/reducers/apiProcessingReducer';
import auth from '@alphapoint/apex-web/lib/redux/reducers/authReducer';
import confirmWithdraw from '@alphapoint/apex-web/lib/redux/reducers/confirmWithdrawReducer';
import instrument from '@alphapoint/apex-web/lib/redux/reducers/instrumentReducer';
import deposit from '@alphapoint/apex-web/lib/redux/reducers/depositReducer';
import level1 from '@alphapoint/apex-web/lib/redux/reducers/level1Reducer';
import level2 from '@alphapoint/apex-web/lib/redux/reducers/level2Reducer';
import loyaltyFee from '@alphapoint/apex-web/lib/redux/reducers/loyaltyFeeReducer';
import modal from '@alphapoint/apex-web/lib/redux/reducers/modalReducer';
import orderHistory from '@alphapoint/apex-web/lib/redux/reducers/orderHistoryReducer';
import paginationMemory from '@alphapoint/apex-web/lib/redux/reducers/paginationReducer';
import product from '@alphapoint/apex-web/lib/redux/reducers/productReducer';
import productManifest from '@alphapoint/apex-web/lib/redux/reducers/productManifestReducer';
import position from '@alphapoint/apex-web/lib/redux/reducers/positionReducer';
import recentTrades from '@alphapoint/apex-web/lib/redux/reducers/recentTradesReducer';
import resetPassword from '@alphapoint/apex-web/lib/redux/reducers/resetPasswordReducer';
import report from '@alphapoint/apex-web/lib/redux/reducers/reportReducer';
import sidePane from '@alphapoint/apex-web/lib/redux/reducers/sidePaneReducer';
import signup from '@alphapoint/apex-web/lib/redux/reducers/signupReducer';
import snackbar from '@alphapoint/apex-web/lib/redux/reducers/snackbarReducer';
import spotAccountInfo from '@alphapoint/apex-web/lib/redux/reducers/spotAccountInfo';
import tickers from '@alphapoint/apex-web/lib/redux/reducers/tickersReducer';
import transfer from '@alphapoint/apex-web/lib/redux/reducers/transferReducer';
import transferRequest from '@alphapoint/apex-web/lib/redux/reducers/transferRequestReducer';
import transferRequestRequested from '@alphapoint/apex-web/lib/redux/reducers/transferRequestRequestedReducer';
import user from '@alphapoint/apex-web/lib/redux/reducers/userReducer';
import verifyEmail from '@alphapoint/apex-web/lib/redux/reducers/verifyEmailReducer';
import verifyAddress from '@alphapoint/apex-web/lib/redux/reducers/verifyAddressReducer';
import verifyDevice from '@alphapoint/apex-web/lib/redux/reducers/verifyDeviceReducer';
import withdraw from '@alphapoint/apex-web/lib/redux/reducers/withdrawReducer';
import wsConnection from '@alphapoint/apex-web/lib/redux/reducers/wsConnectionReducer';
import eotc from '@alphapoint/apex-web/lib/redux/reducers/eotcReducer';
import eotcWithdraw from '@alphapoint/apex-web/lib/redux/reducers/eotcWithdrawReducer';
import eotcSettlement from '@alphapoint/apex-web/lib/redux/reducers/eotcSettlementReducer';
import eotcPrices from '@alphapoint/apex-web/lib/redux/reducers/eotcPriceReducer';
import { marginReducer as margin } from '@alphapoint/apex-web/lib/redux/margin/reducers/marginReducer';
import productAttributes from '@alphapoint/apex-web/lib/redux/reducers/productAttributesReducer';
import interestBearingAccounts from '@alphapoint/apex-web/lib/redux/reducers/interestBearingAccountsReducer';
import { marginNotificationsMiddleware } from '@alphapoint/apex-web/lib/redux/margin/middlewares/marginNotificationsMiddleware';

const composeEnhancers =
  typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
        actionsBlacklist: ['LEVEL1_LOADED', 'LEVEL_2_UPDATES', 'REGISTER_FIELD', 'ADD_RECENT_TRADE'],
        maxAge: 200,
      })
    : compose;

const rootReducer = combineReducers({
  api,
  apexCore,
  apiKey,
  affiliate,
  auth,
  form: formReducer,
  instrument,
  level1,
  level2,
  modal,
  orderHistory,
  product,
  productManifest,
  position,
  recentTrades,
  report,
  sidePane,
  signup,
  snackbar,
  spotAccountInfo,
  user,
  apiProcessing,
  deposit,
  withdraw,
  wsConnection,
  tickers,
  transfer,
  transferRequest,
  paginationMemory,
  verifyEmail,
  verifyAddress,
  verifyDevice,
  confirmWithdraw,
  resetPassword,
  loyaltyFee,
  transferRequestRequested,
  eotc,
  eotcWithdraw,
  eotcSettlement,
  eotcPrices,
  margin,
  productAttributes,
  interestBearingAccounts,
});

const store = createStore(
  rootReducer,
  undefined,
  composeEnhancers(
    applyMiddleware(
      level2monitorMiddleware,
      priceQuantityIncrementMiddleware,
      languageMiddleware,
      authMiddleware,
      placeOrderMiddleware,
      startupMiddleware,
      wsConnectionMiddleware,
      orderFeeMiddleware,
      snackOverrideMiddleware,
      marginNotificationsMiddleware,
      thunk,
    ),
  ),
);

export default store;
